import { useEffect, useState } from 'react';
import type { ActionItem, WsEvent } from '@jerome2/shared';
import { api } from '../api.ts';
import { relative } from '../lib/format.ts';
import { signal } from '../lib/signals.ts';
import { markStale } from '../state/mail.ts';
import { loadCounts } from '../state/tasks.ts';
import { errorToast, toast } from '../state/toast.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, Spinner } from '../ui/primitives.tsx';

// Recente acties: wat er de laatste tijd met mails is gedaan (vlag weg, verplaatst, klaar,
// naar einde dag), met Ongedaan maken voor als een vlag te snel weg was. Live via de websocket.

const UNDOABLE = new Set<ActionItem['kind']>(['flag', 'unflag', 'read', 'unread', 'archive', 'delete', 'move', 'finish', 'eod']);

export function actionText(a: ActionItem): string {
  const folder = typeof a.data?.['folderName'] === 'string' ? (a.data['folderName'] as string) : null;
  switch (a.kind) {
    case 'flag':
      return 'Gevlagd';
    case 'unflag':
      return 'Vlag weg';
    case 'read':
      return 'Gelezen';
    case 'unread':
      return 'Ongelezen';
    case 'archive':
      return 'Gearchiveerd';
    case 'delete':
      return 'Verwijderd';
    case 'move':
      return folder ? `Verplaatst naar ${folder}` : 'Verplaatst';
    case 'finish':
      return 'Klaar';
    case 'eod':
      return 'Naar einde dag';
    case 'eod_remove':
      return 'Uit einde dag';
    case 'send':
      return a.data?.['via'] === 'outlook' ? 'Verzonden via Outlook' : 'Verzonden';
  }
}

function upsert(list: ActionItem[], a: ActionItem): ActionItem[] {
  const rest = list.filter((x) => x.id !== a.id);
  return [a, ...rest].sort((x, y) => y.at.localeCompare(x.at)).slice(0, 50);
}

/** `hideHeader`: in een vel op de telefoon staat de titel al op het vel. */
export function RecentActions(props: { onClose?: (() => void) | undefined; hideHeader?: boolean | undefined }) {
  const [actions, setActions] = useState<ActionItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ actions: ActionItem[] }>('/api/actions?limit=50')
      .then((r) => !cancelled && setActions(r.actions))
      .catch((e: Error) => !cancelled && setError(e.message));
    const onWs = (e: Event) => {
      const ev = (e as CustomEvent<WsEvent>).detail;
      if (ev?.type === 'action') setActions((list) => upsert(list ?? [], ev.action));
    };
    window.addEventListener('jerome-ws', onWs);
    return () => {
      cancelled = true;
      window.removeEventListener('jerome-ws', onWs);
    };
  }, []);

  const undo = async (a: ActionItem) => {
    setBusy(a.id);
    try {
      const r = await api<{ action: ActionItem }>(`/api/actions/${encodeURIComponent(a.id)}/undo`, { method: 'POST' });
      setActions((list) => upsert(list ?? [], r.action));
      markStale();
      void loadCounts().catch(() => undefined);
      toast('Ongedaan gemaakt', { tone: 'ok' });
      signal('undo', `Ongedaan gemaakt: ${actionText(a)}`, { kind: a.kind });
    } catch (e) {
      errorToast(e, 'Niet ongedaan gemaakt');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col min-h-0" aria-label="Recente acties">
      {props.hideHeader ? null : (
        <div className="flex items-center gap-2 px-4 h-11 border-b border-line shrink-0">
          <Icon name="clock" size={16} className="text-ink-soft" />
          <span className="font-medium">Recente acties</span>
          {props.onClose ? (
            <button type="button" aria-label="Sluiten" className="ml-auto h-7 w-7 pointer-coarse:h-11 pointer-coarse:w-11 pointer-coarse:-mr-3 rounded flex items-center justify-center text-muted hover:bg-tint" onClick={props.onClose}>
              <Icon name="x" size={14} />
            </button>
          ) : null}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto scroll-thin">
        {error ? (
          <p className="p-4 text-[13px] text-red">{error}</p>
        ) : actions === null ? (
          <Spinner className="p-4" />
        ) : actions.length === 0 ? (
          <p className="p-4 text-[13px] text-muted">Nog geen acties. Wat je met mails doet (vlag weg, verplaatsen, klaar) komt hier te staan, met ongedaan maken.</p>
        ) : (
          <ul>
            {actions.map((a) => {
              const undone = a.undoneAt !== null;
              return (
                <li key={a.id} className="flex items-start gap-3 px-4 py-2.5 border-b border-line last:border-b-0">
                  <div className={`min-w-0 flex-1 ${undone ? 'line-through text-muted' : ''}`}>
                    <div className="flex items-baseline gap-2 text-[13px]">
                      <span className={`font-medium ${undone ? '' : 'text-ink'}`}>{actionText(a)}</span>
                      {a.source === 'jerome' ? <span className="text-[11px] text-muted rounded bg-tint px-1">Jerome</span> : null}
                      <span className="ml-auto shrink-0 text-[12px] text-muted no-underline">{relative(a.at)}</span>
                    </div>
                    <div className="truncate text-[13px]">{a.subject || '(geen onderwerp)'}</div>
                    {a.fromName ? <div className="truncate text-[12px] text-muted">{a.fromName}</div> : null}
                  </div>
                  {!undone && UNDOABLE.has(a.kind) ? (
                    <Button size="sm" variant="ghost" icon="undo" busy={busy === a.id} onClick={() => void undo(a)} className="shrink-0">
                      Ongedaan maken
                    </Button>
                  ) : undone ? (
                    <span className="shrink-0 text-[12px] text-muted pt-1">ongedaan</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
